import { Breadcrumbs, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useProject, type Project } from '@/api/projects';
import { brand } from '@/theme/theme';

export interface StaffBreadcrumbsProps {
  /** The project this page lives under; the component fetches it for the organization and project levels. Omit on the organization page. */
  projectId?: string;
  /** Shown instead of fetching when the caller already holds the project. */
  project?: Pick<Project, 'public_id' | 'name' | 'organization'>;
  /** The organization on a page that has no project (the organization page itself). */
  organization?: { public_id: string; name: string };
  /** The last, unlinked level: the entity this page shows. */
  current?: string;
}

interface Level {
  key: string;
  label: string;
  link?: { to: '/staff/organizations/$orgId'; params: { orgId: string } } | { to: '/staff/projects/$projectId'; params: { projectId: string } };
}

/** Organization › Project › current entity; the one header line every staff detail page shares. */
export function StaffBreadcrumbs({ projectId, project, organization, current }: StaffBreadcrumbsProps) {
  const fetched = useProject(project ? undefined : projectId);
  const p = project ?? fetched.data;
  const org = organization ?? p?.organization;

  const levels: Level[] = [];
  if (org) {
    levels.push({ key: 'org', label: org.name, link: { to: '/staff/organizations/$orgId', params: { orgId: org.public_id } } });
  }
  if (p) {
    levels.push({ key: 'prj', label: p.name, link: { to: '/staff/projects/$projectId', params: { projectId: p.public_id } } });
  }
  if (current) levels.push({ key: 'cur', label: current });
  if (levels.length === 0) return null;

  // The last level is the page itself; only the levels before it link.
  const last = levels.length - 1;
  return (
    <Breadcrumbs separator="›" separatorMargin={6} styles={{ separator: { color: brand.textMuted } }}>
      {levels.map((level, i) =>
        i < last && level.link ? (
          <Link key={level.key} {...level.link} style={{ color: brand.textMuted, textDecoration: 'none', fontSize: 13 }}>
            {level.label}
          </Link>
        ) : (
          <Text key={level.key} fz={13} fw={600} c={brand.text}>
            {level.label}
          </Text>
        ),
      )}
    </Breadcrumbs>
  );
}
